import { toast } from '@postscript/components';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { ShopRole, User, UserProfile } from '../types';
import {
  AddShopUserPayload,
  addUser,
  deleteUser,
  getShopRoles,
  getUserProfile,
  getUsers,
  inviteUsers,
  updateUserProfile,
  updateUserShopRoles,
  UpdateUserShopRolesPayload,
} from '../usersApiClient';

export const USERS_QUERY_KEY = 'shop_users';
export const PROFILE_QUERY_KEY = 'user_profile';
export const ROLES_QUERY_KEY = 'shop_roles';

const staleTime = 1000 * 60;

export const useGetShopUsers = (
  options?: UseQueryOptions<User[], any, User[], typeof USERS_QUERY_KEY>,
): UseQueryResult<User[]> => {
  return useQuery(USERS_QUERY_KEY, getUsers, {
    ...options,
    staleTime,
  });
};

export const useAddUser = (): UseMutationResult<
  void,
  any,
  AddShopUserPayload
> => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (values: AddShopUserPayload) => addUser(values),
    {
      onSuccess: () => queryClient.refetchQueries(USERS_QUERY_KEY),
      onError: toast.error,
    },
  );

  return mutation;
};

export const useDeleteUser = (): UseMutationResult<void, any, string> => {
  const queryClient = useQueryClient();

  const mutation = useMutation((guid: string) => deleteUser(guid), {
    onSuccess: () => queryClient.refetchQueries(USERS_QUERY_KEY),
    onError: toast.error,
  });

  return mutation;
};

export const useInviteUsers = (): UseMutationResult<
  void,
  any,
  AddShopUserPayload[]
> => {
  return useMutation((users: AddShopUserPayload[]) => inviteUsers(users));
};

export const useGetUserProfile = (
  options?: UseQueryOptions<
    UserProfile,
    any,
    UserProfile,
    typeof PROFILE_QUERY_KEY
  >,
): UseQueryResult<UserProfile> => {
  return useQuery(PROFILE_QUERY_KEY, getUserProfile, {
    ...options,
    staleTime,
  });
};

export const useUpdateUserProfile = (): UseMutationResult<User, any, User> => {
  const queryClient = useQueryClient();

  const mutation = useMutation((user: User) => updateUserProfile(user), {
    onSuccess: (user: User) => {
      const existingUsers =
        queryClient.getQueryData<User[]>(USERS_QUERY_KEY) || [];
      const users = [user, ...existingUsers];

      queryClient.setQueryData(USERS_QUERY_KEY, users);
    },
    onError: toast.error,
  });

  return mutation;
};

export const useGetShopRoles = (
  options?: UseQueryOptions<
    ShopRole[],
    any,
    ShopRole[],
    typeof ROLES_QUERY_KEY
  >,
): UseQueryResult<ShopRole[]> => {
  return useQuery(ROLES_QUERY_KEY, getShopRoles, {
    ...options,
    staleTime,
  });
};

export const useUpdateUserShopRoles = (): UseMutationResult<
  void,
  any,
  UpdateUserShopRolesPayload
> => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({ userId, shopId, roles }: UpdateUserShopRolesPayload) =>
      updateUserShopRoles({ userId, shopId, roles }),
    {
      onSuccess: () => queryClient.refetchQueries(USERS_QUERY_KEY),
      onError: toast.error,
    },
  );

  return mutation;
};
